import Phaser from 'phaser';
import { EnemyType } from '@/config/GameConfig';
import {
  ProceduralMapParams, DifficultyLabel, difficultyForTier,
  unlockCost, waveCount, enemiesForTier, paramsForTier,
} from '@/config/DifficultyScaling';
import { SeededRandom } from '@/map/SeededRandom';

// ── Branch identifiers ──
export type Branch = 'alpha' | 'beta' | 'gamma';

// ── Level tree node ──
export interface LevelNode {
  id: string;
  index: number;           // 1-99
  name: string;
  branch: Branch | 'merge';
  tier: number;            // 0-32 vertical position in tree
  unlockCost: number;      // currency to unlock
  prereqs: string[];       // IDs of prerequisite nodes
  mapSeed: number;         // deterministic seed for map generation
  mapParams: ProceduralMapParams;
  difficulty: DifficultyLabel;
  allowedEnemies: EnemyType[];
  waves: number;
  // Visual position in tree UI (normalized 0-1 for x, tier-based for y)
  treeX: number;
  treeYOffset: number;     // Y jitter within tier (pixels)
}

// ── Thematic name lists per branch ──
// Names: military/robotic/industrial ops — no place references
const ALPHA_NAMES = [
  'Iron Protocol', 'Steel Hammer', 'Anvil Strike', 'Heavy Barrage',
  'Lockdown', 'Broken Gear', 'Forward Push', 'First Contact',
  'Garrison Order', 'Overwatch', 'Blast Shield', 'Deep Impact',
  'Thunder Op', 'Hard Counter', 'Scorched Run', 'Breach & Clear',
  'Siege Protocol', 'Bone Breaker', 'Red Barricade', 'Sentinel Watch',
  'Brave Stance', 'Last Resort', 'War Machine', 'Dusk Offensive',
  'Wreckage Sweep', 'Broken Arrow', 'Final Order', 'Siege Hammer',
  'Steel Trap', 'Arc Surge', 'Burned Ground', 'Iron Charge',
  'Cold Front', 'Dawn Assault',
];
const BETA_NAMES = [
  'Shadow Op', 'Dark Signal', 'Obsidian Edge', 'Phantom Strike',
  'Silent Fog', 'Ghost Recon', 'Veiled Approach', 'Quiet Breach',
  'Night Shift', 'Hidden Freq', 'Deep Cover', 'Hex Protocol',
  'Covert Entry', 'Blackout', 'Echo Pulse', 'Wailing Signal',
  'Soul Drain', 'Stealth Run', 'Dark Sweep', 'Dead Frequency',
  'Whisper Net', 'Onyx Trigger', 'Black Cipher', 'Fog Drift',
  'Grim Intercept', 'Core Breach', 'Forbidden Link', 'Sunken Data',
  'Dark Threshold', 'Glacial Hack', 'Maze Runner', 'Cryptic Scan',
  'Spirit Tap', 'Lost Archive',
];
const GAMMA_NAMES = [
  'Swarm Alert', 'Hive Override', 'Locust Surge', 'Drone Blitz',
  'Colony Burst', 'Wild Spawn', 'Teeming Rush', 'Living Flood',
  'Hex Cluster', 'Infernal Burst', 'Web Trap', 'Venom Spray',
  'Night Swarm', 'Buzz Kill', 'Larva Bloom', 'Spore Blast',
  'Toxic Flood', 'Bio Hazard', 'Parasite Scan', 'Moth Signal',
  'Necro Swarm', 'Tidal Spawn', 'Canopy Rot', 'Root Hack',
  'Nest Purge', 'Feral Flock', 'Infested Run', 'Royal Sting',
  'War Cocoon', 'Giant Hatch', 'Organic Pulse', 'Green Meltdown',
  'Putrid Surge', 'Parasite Bloom',
];
const MERGE_NAMES = [
  'Nexus Link', 'Convergence', 'Crossfire', 'Junction Lock',
  'Clash Point', 'Dual Front', 'Confluence', 'Epicenter',
  'Grand Op', 'Gravity Pull', 'Forced Sync', 'Ground Zero',
  'Critical Mass', 'Central Lock', 'Vital Pulse', 'Maelstrom',
  'Final Push', 'Gate Burst', 'Vortex Sync', 'Common Front',
];

function pickName(rng: SeededRandom, branch: Branch | 'merge'): string {
  const list = branch === 'alpha' ? ALPHA_NAMES
    : branch === 'beta' ? BETA_NAMES
    : branch === 'gamma' ? GAMMA_NAMES
    : MERGE_NAMES;
  return rng.pick(list);
}

// ── Tree topology builder ──
// 3 branches: alpha (left), beta (center), gamma (right)
// Merges at tiers 11, 22, 32 (final)
// Each tier has 1-3 nodes, ~3 nodes per tier = ~99 nodes over 33 tiers

function buildTree(): LevelNode[] {
  const nodes: LevelNode[] = [];
  let idx = 1;
  const MERGE_TIERS = new Set([11, 22, 32]);
  // Tiers where we add a bonus cross-branch node (alongside the 3 branch nodes = 4 total)
  const BONUS_TIERS = new Set([5, 8, 14, 17, 20, 25, 28, 30]);

  // Base branch X positions (will be jittered per-node for organic look)
  const branchBaseX: Record<Branch, number> = { alpha: 0.2, beta: 0.5, gamma: 0.8 };

  // Master RNG for layout jitter (deterministic)
  const layoutRng = new SeededRandom(42_007);

  for (let tier = 0; tier <= 32; tier++) {
    if (tier === 0) {
      // Single starting node (centered, no jitter)
      nodes.push(makeNode(idx++, 'merge', tier, 0.5, 0, []));
      continue;
    }

    if (MERGE_TIERS.has(tier)) {
      // Merge node: positioned at average X of prereqs for organic feel
      const prevTier = nodes.filter(n => n.tier === tier - 1);
      const prereqs = prevTier.map(n => n.id);
      const avgX = prevTier.reduce((s, n) => s + n.treeX, 0) / prevTier.length;
      const jitterX = (layoutRng.next() - 0.5) * 0.08;
      const jitterY = (layoutRng.next() - 0.5) * 20;
      nodes.push(makeNode(idx++, 'merge', tier,
        Phaser.Math.Clamp(avgX + jitterX, 0.15, 0.85), jitterY, prereqs, true));
      continue;
    }

    // Regular tier: one node per branch with organic jitter
    const prevTier = nodes.filter(n => n.tier === tier - 1);

    for (const branch of ['alpha', 'beta', 'gamma'] as Branch[]) {
      const prereq = prevTier.find(n => n.branch === branch || n.branch === 'merge');
      const prereqs = prereq ? [prereq.id] : [];

      // Organic X position: base + jitter, attracted toward prereq
      const baseX = branchBaseX[branch];
      const jitterX = (layoutRng.next() - 0.5) * 0.15;
      const prereqAttract = prereq ? (prereq.treeX - baseX) * 0.2 : 0;
      const finalX = Phaser.Math.Clamp(baseX + jitterX + prereqAttract, 0.06, 0.94);
      const jitterY = (layoutRng.next() - 0.5) * 24;

      nodes.push(makeNode(idx++, branch, tier, finalX, jitterY, prereqs));
    }

    // Bonus cross-branch node at selected tiers
    // These depend on same-tier branch nodes, so visually offset upward by half a tier
    if (BONUS_TIERS.has(tier)) {
      const alphaNode = nodes.find(n => n.tier === tier && n.branch === 'alpha');
      const gammaNode = nodes.find(n => n.tier === tier && n.branch === 'gamma');
      const bonusPrereqs = [alphaNode?.id, gammaNode?.id].filter(Boolean) as string[];
      // Position between alpha and gamma with jitter
      const midX = ((alphaNode?.treeX ?? 0.3) + (gammaNode?.treeX ?? 0.7)) / 2;
      const jitterX = (layoutRng.next() - 0.5) * 0.1;
      // Offset upward slightly so it appears ABOVE its same-tier prereqs (clamped by collision pass)
      const yOffset = -30 + (layoutRng.next() - 0.5) * 10;
      nodes.push(makeNode(idx++, 'merge', tier,
        Phaser.Math.Clamp(midX + jitterX, 0.2, 0.8), yOffset, bonusPrereqs, true));
    }
  }

  // ── Collision resolution ──
  // Phase 1: same-tier X gap (normalized X)
  const MIN_X_GAP = 0.30; // ~180px at 600px usable width (nodes are 150px wide + gap)
  const tierMap = new Map<number, LevelNode[]>();
  for (const n of nodes) {
    if (!tierMap.has(n.tier)) tierMap.set(n.tier, []);
    tierMap.get(n.tier)!.push(n);
  }
  for (const tierNodes of tierMap.values()) {
    if (tierNodes.length < 2) continue;
    tierNodes.sort((a, b) => a.treeX - b.treeX);
    for (let pass = 0; pass < 5; pass++) {
      for (let i = 0; i < tierNodes.length - 1; i++) {
        const gap = tierNodes[i + 1].treeX - tierNodes[i].treeX;
        if (gap < MIN_X_GAP) {
          const push = (MIN_X_GAP - gap) / 2 + 0.005;
          tierNodes[i].treeX = Math.max(0.04, tierNodes[i].treeX - push);
          tierNodes[i + 1].treeX = Math.min(0.96, tierNodes[i + 1].treeX + push);
        }
      }
      tierNodes.sort((a, b) => a.treeX - b.treeX);
    }
  }

  // Phase 2: cross-tier 2D overlap resolution (pixel-space)
  // Prevents nodes from overlapping across tiers (Y offsets + same-tier bonus nodes)
  // Must match LevelTreeScene: TREE_W=760, pad=80 → usable=600, NODE_SPACING_Y=150
  const TREE_W_USABLE = 600;
  const NODE_SPACING_Y = 150;
  const MIN_DIST_X = 170;     // node is 150px wide + 20px gap
  const MIN_DIST_Y = 90;      // node is 52px + difficulty badge ~22px + 16px gap

  // Convert to approximate pixel positions for overlap checks
  const pixelPos = (n: LevelNode) => ({
    px: n.treeX * TREE_W_USABLE,
    py: n.tier * NODE_SPACING_Y + (n.treeYOffset ?? 0),
  });

  // Clamp Y offset so nodes don't drift too far from their tier
  const MAX_Y_OFFSET = 40;

  for (let pass = 0; pass < 10; pass++) {
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i];
      const posA = pixelPos(a);
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        // Only check nodes within 2 tiers of each other
        if (Math.abs(a.tier - b.tier) > 2) continue;
        const posB = pixelPos(b);
        const dx = Math.abs(posA.px - posB.px);
        const dy = Math.abs(posA.py - posB.py);
        // If both X and Y are too close, nodes overlap
        if (dx < MIN_DIST_X && dy < MIN_DIST_Y) {
          // Push apart in Y
          const pushY = (MIN_DIST_Y - dy) / 2 + 4;
          if (posA.py < posB.py) {
            a.treeYOffset -= pushY;
            b.treeYOffset += pushY;
          } else {
            a.treeYOffset += pushY;
            b.treeYOffset -= pushY;
          }
          // Also try pushing apart in X if same tier
          if (a.tier === b.tier && dx < MIN_DIST_X) {
            const pushX = (MIN_DIST_X - dx) / (2 * TREE_W_USABLE) + 0.01;
            if (posA.px < posB.px) {
              a.treeX = Math.max(0.04, a.treeX - pushX);
              b.treeX = Math.min(0.96, b.treeX + pushX);
            } else {
              a.treeX = Math.max(0.04, a.treeX + pushX);
              b.treeX = Math.min(0.96, b.treeX - pushX);
            }
          }
        }
      }
    }
    // Clamp Y offsets after each pass
    for (const n of nodes) {
      n.treeYOffset = Math.max(-MAX_Y_OFFSET, Math.min(MAX_Y_OFFSET, n.treeYOffset));
    }
  }

  return nodes;

  function makeNode(
    i: number, branch: Branch | 'merge', tier: number,
    treeX: number, treeYOffset: number, prereqs: string[], isMerge = false,
  ): LevelNode {
    const seed = 7919 * i + 104729;
    const nameRng = new SeededRandom(seed);
    const variation = branch === 'alpha' ? 0 : branch === 'beta' ? 1 : 2;
    const params = paramsForTier(tier, branch === 'merge' ? 0 : variation);

    return {
      id: `level_${String(i).padStart(3, '0')}`,
      index: i,
      name: pickName(nameRng, branch),
      branch,
      tier,
      unlockCost: tier === 0 ? 0 : unlockCost(tier, isMerge),
      prereqs,
      mapSeed: seed,
      mapParams: params,
      difficulty: difficultyForTier(tier),
      allowedEnemies: enemiesForTier(tier),
      waves: waveCount(tier),
      treeX,
      treeYOffset,
    };
  }
}

/** All 99 level nodes in the tree (built once, immutable) */
export const LEVEL_TREE: LevelNode[] = buildTree();

/** Quick lookup by ID */
export const LEVEL_MAP: Map<string, LevelNode> = new Map(
  LEVEL_TREE.map(n => [n.id, n]),
);

/** Demo cap: levels above this index are locked in non-DEV mode */
export const DEMO_LEVEL_CAP = 20;

/** Is this node beyond the demo cap? */
export function isDemoCapped(node: LevelNode): boolean {
  return node.index > DEMO_LEVEL_CAP;
}
